/**
 * Ayurveda parsing & summarization utilities.
 *
 * Extracted from ConsultationPage.tsx as part of the Ayurveda module separation.
 * These functions parse Ayurveda-specific data (dosha, desa, nidana, diet,
 * lifestyle) from DiagnosticReport conclusions / SOAP compositions and build
 * the PA data sections shown to Ayurveda practitioners.
 *
 * Generic clinical-text helpers are imported from the shared
 * `@/lib/utils/clinicalText` module (used by both flows).
 */

import type { AyurvedaData } from '@/ayurveda/types/ayurveda';
import type { SoapNoteData } from '@/lib/api/medplum-resources';
import { stripHtml } from '@/lib/api/medplum-resources';
import { createComponentLogger } from '@/lib/utils/logger';
import {
  isRecord,
  getStringField,
  getArrayField,
  sentenceCase,
  extractMatchingSentences,
  extractMatchingClauses,
  splitClinicalItems,
  compactItems,
} from '@/lib/utils/clinicalText';
import { PRAKRUTHI_DOSHA_OPTIONS, AAMA_OPTIONS } from '@/ayurveda/config/clinicalAssessmentOptions';

const logger = createComponentLogger('parseAyurveda');

// ---------------------------------------------------------------------------
// Free-text -> chip-option normalization
//
// The newer PA schema's "Ayurveda Context" JSON reports prakriti/vikriti as
// free descriptive text (e.g. "Vata-Pitta", "Pitta aggravation") rather than
// the fixed Vatha/Pitha/Kapha/... option strings the Prakruthi/Dosha chip
// groups expect. These helpers detect which dosha(s) a free-text value
// mentions and map that combination onto the exact chip option label, so the
// existing chip-highlighting UI can reflect this data without redesigning it.
// ---------------------------------------------------------------------------

export function doshaToChipValue(value?: string | null): string | null {
  if (!value) return null;
  const lower = value.toLowerCase();
  const hasVata = /vat[ah]/.test(lower);
  const hasPitta = /pit[ht]a/.test(lower);
  const hasKapha = /kapha/.test(lower);
  const count = [hasVata, hasPitta, hasKapha].filter(Boolean).length;
  if (count === 0) return null;
  if (count === 3) return matchOption('Sannipatika');
  if (count === 2) {
    if (hasVata && hasKapha) return matchOption('Vathakapha');
    if (hasVata && hasPitta) return matchOption('Vathapitha');
    return matchOption('Kapha Pitha');
  }
  if (hasVata) return matchOption('Vatha');
  if (hasPitta) return matchOption('Pitha');
  return matchOption('Kapha');
}

function matchOption(canonical: string): string | null {
  return PRAKRUTHI_DOSHA_OPTIONS.find((o) => o === canonical) ?? null;
}

export function amaToChipValue(value?: string | null): string | null {
  if (!value) return null;
  const lower = value.toLowerCase();
  if (/\b(none|nil|absent|negative|not\s+present|no\s+(?:evidence\s+of\s+)?a?ama)\b/.test(lower)) {
    return AAMA_OPTIONS.find((o) => o === 'Absent') ?? null;
  }
  return AAMA_OPTIONS.find((o) => o === 'Present') ?? null;
}

// ---------------------------------------------------------------------------
// Ayurveda-specific summarization helpers
// ---------------------------------------------------------------------------

export function summarizeLifestyle(lifestyleSummary?: string | null): string[] {
  return compactItems(
    splitClinicalItems(lifestyleSummary).filter((item) =>
      /sleep|stress|office|weight|pulse|lifestyle|pressure|regular/i.test(item)
    ),
    6
  );
}

export function summarizeCommonFoods(items: string[], dietSummary?: string | null): string[] {
  const source = items.length > 0 ? items : splitClinicalItems(dietSummary);
  const foodItems = source.flatMap((item) => {
    const normalized = item.toLowerCase();
    const bullets: string[] = [];

    if (/spicy/.test(normalized)) bullets.push('Spicy food preference');
    if (/deep-fried|fried/.test(normalized)) bullets.push('Deep-fried foods');
    if (/snacks?/.test(normalized)) bullets.push('Frequent snacks/cravings');
    if (/buttermilk/.test(normalized)) bullets.push('Buttermilk provides cooling relief');
    if (bullets.length === 0) bullets.push(sentenceCase(item));

    return bullets;
  });

  return compactItems(foodItems, 6);
}

export function summarizeKnownHistory(items: string[]): string[] {
  const text = items.join(' ');
  const bullets: string[] = [];

  const bpMatch = text.match(/(?:high blood pressure|blood pressure)[^.,;)]*(?:\([^)]*\))?/i);
  if (bpMatch) bullets.push(sentenceCase(bpMatch[0]));

  const pulseMatch = text.match(/elevated pulse[^.,;)]*(?:\([^)]*\))?/i);
  if (pulseMatch) bullets.push(sentenceCase(pulseMatch[0]));

  if (/no thyroid/i.test(text)) bullets.push('No thyroid issues reported');
  if (/no .*diabetes|not .*diabetes/i.test(text)) bullets.push('No diabetes reported');
  if (/not currently taking any medications|no current medications/i.test(text)) {
    bullets.push('No current medications');
  }

  return compactItems(bullets.length > 0 ? bullets : items, 6);
}

export function summarizeFamilyHistory(items: string[]): string[] {
  const text = items.join(' ');
  const bullets: string[] = [];

  if (/father.*gastro|gastro.*father/i.test(text)) {
    bullets.push('Father has gastroenterological history');
  }
  if (/family history.*gastro|gastro.*family history/i.test(text)) {
    bullets.push('Family GI history suggests predisposition');
  }
  if (/hereditary|predisposition/i.test(text) && bullets.length === 0) {
    bullets.push('Hereditary predisposition noted');
  }

  return compactItems(bullets.length > 0 ? bullets : items, 4);
}

export function summarizeAssessment(assessment?: string | null): string[] {
  const text = assessment ?? '';
  const normalized = text.toLowerCase();
  const bullets: string[] = [];

  if (/pitta aggravation|aggravated pitta/.test(normalized)) bullets.push('Pitta aggravation');
  if (/burning sensation/.test(normalized)) bullets.push('Burning sensation after food');
  if (/stress|office pressure|irregular sleep/.test(normalized)) {
    bullets.push('Stress and irregular sleep imbalance Pitta/Vata');
  }
  if (/spicy|deep-fried|pitta-aggravating/.test(normalized)) {
    bullets.push('Spicy/deep-fried food aggravation');
  }
  if (/buttermilk/.test(normalized)) bullets.push('Relief with buttermilk');
  if (/family history|predisposition/.test(normalized)) {
    bullets.push('Family GI history suggests predisposition');
  }
  if (/amlapitta|gerd/.test(normalized)) bullets.push('Amlapitta/GERD differential');
  if (/parinama shoola/.test(normalized)) bullets.push('Parinama Shoola differential');
  if (/annadrava shoola/.test(normalized)) bullets.push('Annadrava Shoola differential');

  return compactItems(bullets.length > 0 ? bullets : splitClinicalItems(assessment), 8);
}

export function summarizeTreatmentPlan(plan?: string | null): string[] {
  const text = plan ?? '';
  const normalized = text.toLowerCase();
  const bullets: string[] = [];

  if (/cbc|liver function|kidney function|endoscopy|h\.?\s*pylori/.test(normalized)) {
    bullets.push('Consider CBC, LFT, KFT, upper endoscopy, and H. pylori testing');
  }
  if (/avoid.*spicy|deep-fried|sour/.test(normalized)) {
    bullets.push('Avoid spicy, deep-fried, and excessively sour foods');
  }
  if (/regular meal|smaller, frequent|frequent meals/.test(normalized)) {
    bullets.push('Follow regular meal timings and smaller frequent meals');
  }
  if (/cooling foods|cucumber|coconut water|sweet fruits/.test(normalized)) {
    bullets.push('Use cooling foods like cucumber, coconut water, and sweet fruits');
  }
  if (/stress management|meditation|yoga/.test(normalized)) {
    bullets.push('Stress management with meditation or yoga');
  }
  if (/avipattikar|kamadudha|praval pishti/.test(normalized)) {
    bullets.push('Consider Pitta-pacifying formulations under professional guidance');
  }
  if (/red flags|severe abdominal pain|blood in vomit|difficulty swallowing/.test(normalized)) {
    bullets.push('Escalate for red flags: severe pain, vomiting, bleeding, weight loss, dysphagia');
  }
  if (/follow-up|follow up|followup/.test(normalized)) {
    bullets.push('Follow up with an Ayurvedic physician for personalized care');
  }

  return compactItems(bullets.length > 0 ? bullets : splitClinicalItems(plan), 8);
}

// Shared alternation of labels that can terminate a labelled clause in the
// SOAP subjective narrative (e.g. "Chief complaint: ... Duration: ... Mode of
// Onset: ... History: ... Past Illness: ..."). Every extractor below stops at
// the next one of these labels so labelled clauses don't bleed into each other.
const NARRATIVE_LABEL_TERMINATORS =
  'Objective|Assessment|Plan|History|Nidana|Diet|Lifestyle|Allergies|Medications|' +
  'Past|Family|Social|Physical|Skin|Agni|Ama|Red flags|Follow|Evaluation|' +
  'Duration|Mode of Onset|Treatment History|Current Medication';

/**
 * Extract a `${label}:`-labelled clause from a SOAP subjective narrative,
 * capturing everything up to the next labelled section / sentence boundary.
 * Shared by extractHistory, extractDuration, extractModeOfOnset, etc.
 */
function extractLabelledClause(text: string | null | undefined, label: string): string | null {
  if (!text) return null;
  const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = text.match(
    new RegExp(
      `\\b${escapedLabel}\\s*:\\s*([\\s\\S]+?)(?=\\n\\s*(?:${NARRATIVE_LABEL_TERMINATORS})\\s*:|$)`,
      'i'
    )
  );
  return match ? match[1].trim() : null;
}

/**
 * Extract the text of an `<li><strong>{label}:</strong> ...</li>` bullet from
 * a raw (unstripped) HTML section — e.g. "Vikriti Baseline Assessment", which
 * is a single <ul> of <strong>Label:</strong> value bullets. Sibling to
 * extractLabelledClause, which extracts "Label:"-prefixed clauses from plain
 * narrative text instead of HTML bullets.
 */
function extractHtmlListItem(html: string | null | undefined, label: string): string | null {
  if (!html) return null;
  const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = html.match(
    new RegExp(
      `<li>\\s*<strong>\\s*${escapedLabel}\\s*:?\\s*<\\/strong>\\s*([\\s\\S]*?)<\\/li>`,
      'i'
    )
  );
  if (!match) return null;
  return stripHtml(match[1]).replace(/\n+/g, ' ').trim() || null;
}

export function summarizeChiefComplaint(subjective?: string | null): string[] {
  // The PA Data Preview must show the SAME chief complaint the Case Sheet shows.
  // The Case Sheet reads the structured `chief_complaint` field directly; the PA
  // preview receives the full SOAP `subjective` blob, which always begins with a
  // "Chief complaint:" (or "Chief Complaint:") labelled sentence. Extract that
  // labelled content rather than keyword-guessing the whole subjective — the
  // previous keyword approach matched unrelated words like "backbone" → "Back
  // discomfort" and produced a complaint unrelated to the real one.
  if (!subjective) return [];

  // Match a leading "Chief complaint:" label (case-insensitive), capturing
  // everything up to the next labelled section / sentence boundary.
  const ccMatch = subjective.match(
    /^\s*Chief\s+complaint\s*:\s*([\s\S]+?)(?=\n\s*(?:Objective|Assessment|Plan|History|Nidana|Diet|Lifestyle|Allergies|Medications|Past|Family|Social|Physical|Skin|Agni|Ama|Red flags|Follow|Evaluation)\s*:|$)/i
  );

  let ccText: string | null = ccMatch ? ccMatch[1].trim() : null;

  // Fallback: if there is no explicit label, take the first sentence of the
  // subjective (the SOAP convention is that the chief complaint comes first).
  if (!ccText) {
    const firstSentence = subjective.split(/[.;\n]/)[0]?.trim();
    ccText = firstSentence && firstSentence.length > 0 ? firstSentence : null;
  }

  if (!ccText) return [];

  // Split the chief-complaint text into concise bullets. The CC is usually
  // one or two sentences; split on semicolons / commas / "and" / "accompanied by"
  // so each clinical facet becomes its own bullet, then trim and de-duplicate.
  const facets = ccText
    .split(/;|,(?=\s)|\band\b|\baccompanied by\b|\bwith\b/i)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const bullets = compactItems(
    facets.map((f) => sentenceCase(f.replace(/[.;]+$/g, '').trim())),
    5
  );

  // Final fallback: if splitting produced nothing usable, show the whole CC
  // (capped) so the section is never empty.
  if (bullets.length === 0) {
    const trimmed = ccText.slice(0, 150);
    return trimmed ? [sentenceCase(trimmed)] : [];
  }

  return bullets;
}

export function extractKnownHistory(parsed: Record<string, unknown>): string[] {
  const subjective = getStringField(parsed.subjective);
  const objective = getStringField(parsed.objective);
  // Clause-level, negation-aware extraction so run-on narrative sentences don't
  // leak unrelated content (diet, sleep, complaint) into Known Diseases.
  const medicalHistory = extractMatchingClauses(
    [subjective, objective],
    [
      /\b(high blood pressure|blood pressure|bp|hypertension)\b/i,
      /\b(elevated pulse|pulse|heart rate|tachycardia)\b/i,
      /\bthyroid\b/i,
      /\bdiabetes|diabetic\b/i,
      /\bmedication|medications|medicine|medicines\b/i,
      /\bchronic\b/i,
    ],
    8
  );

  return medicalHistory.map(sentenceCase);
}

export function extractFamilyHistory(parsed: Record<string, unknown>): string[] {
  // Prefer the explicit structured array from the agent summary if present.
  const explicit = getArrayField(parsed.family_history ?? parsed.familyHistory);
  if (explicit.length > 0) return explicit;

  // Fallback: clause-level, negation-aware extraction from narrative text.
  // Using clauses (not whole sentences) prevents diet/sleep/complaint narrative
  // from a run-on subjective sentence leaking into Family History, and the
  // negation filter drops phrases like "no significant family history".
  return extractMatchingClauses(
    [getStringField(parsed.subjective), getStringField(parsed.assessment)],
    [
      /\bfather\b/i,
      /\bmother\b/i,
      /\b(sibling|brother|sister)\b/i,
      /\bfamily history\b/i,
      /\bfamilial\b/i,
      /\bhereditary\b/i,
      /\bpredisposition\b/i,
    ],
    5
  ).map(sentenceCase);
}

// When nothing about diet was said, the CDSS agent's model sometimes fills a
// food/diet field with narration about the rest of the consultation, and can
// leak its own output-schema keys into it (e.g. "Patient reported fever and
// cough during consultation, follow_up_questions"). None of that is a food, so
// drop it before it reaches Diet. Deliberately narrow: genuine diet sentences
// ("Patient has a vegetarian diet", "Patient denies eating meat") must survive.
const NON_FOOD_TEXT_PATTERNS = [
  /\b[a-z]+(?:_[a-z]+)+\b/, // snake_case schema key, e.g. follow_up_questions
  /\bduring (?:the )?(?:consultation|conversation|visit)\b/i,
];

export function isSchemaLeakOrNarration(text: string): boolean {
  return NON_FOOD_TEXT_PATTERNS.some((pattern) => pattern.test(text));
}

export function filterFoodEntries(items: readonly unknown[] | null | undefined): string[] {
  return (items ?? []).filter(
    (item): item is string =>
      typeof item === 'string' && item.trim() !== '' && !isSchemaLeakOrNarration(item)
  );
}

/**
 * Diet text with leaked/narrative parts removed. Clean text is returned
 * untouched — only text that actually trips the filter is split on commas, so
 * a sentence like "Patient denies eating meat, eggs" keeps its context.
 */
export function sanitizeDietText(text: string | null | undefined): string | null {
  const trimmed = text?.trim();
  if (!trimmed) return null;
  if (!isSchemaLeakOrNarration(trimmed)) return trimmed;
  const kept = filterFoodEntries(trimmed.split(',').map((part) => part.trim()));
  return kept.length > 0 ? kept.join(', ') : null;
}

export function extractCommonlyEatenFood(
  parsed: Record<string, unknown>,
  dietSummary: string | null
): string[] {
  const explicitFoods = filterFoodEntries(
    getArrayField(parsed.commonly_eaten_food ?? parsed.commonlyEatenFood)
  );
  if (explicitFoods.length > 0) return explicitFoods;

  const dietSentences = filterFoodEntries(
    extractMatchingSentences(
      [dietSummary],
      [/\b(spicy|deep-fried|fried|snacks|food|diet|craving|sour|buttermilk)\b/i],
      4
    )
  );

  return dietSentences.map(sentenceCase);
}

// ---------------------------------------------------------------------------
// Ayurveda data builders / parsers
// ---------------------------------------------------------------------------

/**
 * Parse a Composition section's raw value (a JSON string, an already-parsed
 * object, or absent) into a plain record. Shared by every JSON-carrying
 * section (ayurveda_context, desa_information, registration, personal_history,
 * astha_sthaana_pareeksha, prakruthi_dosha, diagnosis, treatment) so the
 * string-vs-object handling isn't duplicated per section.
 */
function parseJsonSection(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === 'string' && raw.trim()) {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return null;
}

/**
 * Extract a "History:"-labelled sentence from the SOAP Subjective narrative,
 * for the Patient Information card. Sibling to summarizeChiefComplaint above,
 * which uses the same regex-label-extraction technique for "Chief complaint:".
 * NARRATIVE_LABEL_TERMINATORS already includes "History" as a stop-word,
 * confirming the SOAP prompt emits this label.
 */
export function extractHistory(subjective?: string | null): string | null {
  return extractLabelledClause(subjective, 'History');
}

/** Extract the "Duration:"-labelled clause, for Chief Complaint's onset details. */
export function extractDuration(subjective?: string | null): string | null {
  return extractLabelledClause(subjective, 'Duration');
}

/** Extract the "Mode of Onset:"-labelled clause, for Chief Complaint's onset details. */
export function extractModeOfOnset(subjective?: string | null): string | null {
  return extractLabelledClause(subjective, 'Mode of Onset');
}

/** Extract the "Past Illness:" (or "History of Past Illness:") labelled clause. */
export function extractPastIllness(subjective?: string | null): string | null {
  return (
    extractLabelledClause(subjective, 'History of Past Illness') ??
    extractLabelledClause(subjective, 'Past Illness')
  );
}

/** Extract the "Treatment History:" labelled clause. */
export function extractTreatmentHistory(subjective?: string | null): string | null {
  return extractLabelledClause(subjective, 'Treatment History');
}

/**
 * Extract the "Family History:"-labelled clause for the Patient Information
 * card's History section. Distinct from extractFamilyHistory above, which
 * builds a keyword-matched bullet list for the Case Sheet from the whole
 * narrative rather than a single labelled clause.
 */
export function extractHistoryFamilyHistory(subjective?: string | null): string | null {
  return extractLabelledClause(subjective, 'Family History');
}

/** Extract the "Current Medication:" labelled clause. */
export function extractCurrentMedication(subjective?: string | null): string | null {
  return extractLabelledClause(subjective, 'Current Medication');
}

/** Extract the "Family/Social/Occupational:" labelled clause. */
export function extractFamilySocialOccupational(subjective?: string | null): string | null {
  return (
    extractLabelledClause(subjective, 'Family/Social/Occupational') ??
    extractLabelledClause(subjective, 'Family/ Social/ Occupational') ??
    extractLabelledClause(subjective, 'Social History')
  );
}

export function buildAyurvedaDataFromSoapData(
  soapData: SoapNoteData | null | undefined
): AyurvedaData | null {
  if (!soapData) return null;

  // Extract Chief Complaint from flat sections (new format)
  const chiefComplaintSection = soapData.sections?.find(
    (s) => s.title?.toLowerCase() === 'chief complaint'
  );
  const chiefComplaintText = chiefComplaintSection
    ? stripHtml(chiefComplaintSection.content)
    : null;

  // Build a flat lookup from sections for extraction helpers
  const sectionText = (title: string): string | null => {
    const section = soapData.sections?.find((s) => s.title?.toLowerCase() === title.toLowerCase());
    return section ? stripHtml(section.content) : null;
  };

  // Same lookup but returns the raw (unstripped) HTML — needed to regex-match
  // `<li><strong>Label:</strong> ...</li>` bullets before stripping removes
  // the tags extractHtmlListItem depends on.
  const sectionRawHtml = (title: string): string | null => {
    const section = soapData.sections?.find((s) => s.title?.toLowerCase() === title.toLowerCase());
    return section ? section.content : null;
  };

  // When composition exists, always return the struct even if all fields are empty.
  // This ensures the Ayurveda panel skeleton is always visible when the server returns
  // composition data, satisfying the UX requirement of never showing a blank screen.
  const parsed: Record<string, unknown> = {
    chiefComplaint: chiefComplaintText,
    subjective: chiefComplaintText,
    objective: sectionText('objective') ?? sectionText('physical examination'),
    assessment: sectionText('assessment') ?? sectionText('red flags'),
    plan: sectionText('plan'),
    family_history: sectionText('family history'),
    commonly_eaten_food: sectionText('diet') ?? sectionText('commonly eaten food'),
  };

  // Ayurveda context — comes from the AYURVEDA_CONTEXT Composition section.
  const acRaw = soapData.ayurveda_context ?? soapData.ayurvedaContext ?? null;
  const ac = parseJsonSection(acRaw);
  const doshaProfile =
    ac?.prakriti || ac?.vikriti
      ? {
          prakriti: getStringField(ac?.prakriti),
          vikriti: getStringField(ac?.vikriti),
          manasikGuna: null,
        }
      : null;
  const agni = getStringField(ac?.agni_status);
  const ama = getStringField(ac?.ama_grade);
  // nidana may be stored as an array or a comma-joined string
  const nidana = Array.isArray(ac?.nidana)
    ? (ac.nidana as string[]).filter(Boolean).join(', ') || null
    : getStringField(ac?.nidana as string | null | undefined);
  const dietSummary = getStringField(ac?.diet_summary);
  const lifestyleSummary = getStringField(ac?.lifestyle_summary);

  // Desa information — comes from the DESA_INFORMATION Composition section.
  const diRaw = soapData.desa_information ?? soapData.desaInformation ?? null;
  const di = parseJsonSection(diRaw);
  const regionalDietRaw = isRecord(di?.regional_diet) ? di.regional_diet : null;
  const desaInformation = di
    ? {
        state: getStringField(di.state),
        city: getStringField(di.city),
        season: getStringField(di.season),
        desaType: getStringField(di.desa_type),
        temperature: typeof di.temperature === 'number' ? di.temperature : null,
        humidity: typeof di.humidity === 'number' ? di.humidity : null,
        stapleDiet: getStringField(di.staple_diet),
        regionalDiet: regionalDietRaw
          ? {
              grains: getStringField(regionalDietRaw.grains),
              pulses: getStringField(regionalDietRaw.pulses),
              fruits: getStringField(regionalDietRaw.fruits),
              veg: getStringField(regionalDietRaw.veg),
              dairy: getStringField(regionalDietRaw.dairy),
              spiceLevel:
                typeof regionalDietRaw.spice_level === 'number'
                  ? regionalDietRaw.spice_level
                  : null,
            }
          : null,
      }
    : null;

  // New PA Composition sections (Patient Information / Ayurvedic Assessment
  // base snapshot). Each is optional and null-tolerant — the kiosk PA only
  // populates working-hypothesis values, so most of these are frequently null.
  const narrativeText = chiefComplaintText ?? sectionText('subjective');
  const history = extractHistory(narrativeText);
  const duration = extractDuration(narrativeText);
  const modeOfOnset = extractModeOfOnset(narrativeText);

  // The backend Composition format evolved from one continuous narrative blob
  // with inline "Label: value" clauses (extracted via extractLabelledClause
  // below) to separate, cleanly-titled sections. Prefer the new sections;
  // fall back to the old inline-label extraction so compositions still using
  // the previous format keep working unchanged.
  const pastIllness = sectionText('past medical history') ?? extractPastIllness(narrativeText);
  const treatmentHistory = extractTreatmentHistory(narrativeText); // no new-schema source
  const historyFamilyHistory =
    (parsed.family_history as string | null) ?? extractHistoryFamilyHistory(narrativeText);
  const currentMedication =
    sectionText('current medications') ?? extractCurrentMedication(narrativeText);
  const familySocialOccupational = extractFamilySocialOccupational(narrativeText); // no new-schema source

  const regRaw = parseJsonSection(soapData.registration);
  const registration = regRaw
    ? {
        opNo: getStringField(regRaw.op_no),
        date: getStringField(regRaw.date),
        legalName: getStringField(regRaw.legal_name),
        age: getStringField(regRaw.age),
        gender: getStringField(regRaw.gender),
        address: getStringField(regRaw.address),
        phone: getStringField(regRaw.phone),
        email: getStringField(regRaw.email),
      }
    : null;

  // "Appetite"/"Bowel Movements" have no JSON source in the new schema — pull
  // them from the Vikriti Baseline Assessment bullet list. "Diet"/"Sleep" reuse
  // the already-parsed dietSummary/lifestyleSummary (full descriptive sentences
  // from Ayurveda Context) rather than the raw bullet fragment or an exact
  // "Diet & Lifestyle" section-title match, since the backend's JSON literally
  // HTML-escapes that title's "&" (`"Diet &amp; Lifestyle"`), which a plain
  // case-insensitive title match won't reliably hit — dietSummary/lifestyleSummary
  // sidestep that entirely and are also simply better UX (full sentences, not
  // bullet fragments).
  const vikritiBaselineHtml = sectionRawHtml('vikriti baseline assessment');
  const phAppetite = extractHtmlListItem(vikritiBaselineHtml, 'Appetite');
  const phBowel = extractHtmlListItem(vikritiBaselineHtml, 'Bowel Movements');
  const phDiet = dietSummary ?? sectionText('diet & lifestyle');
  const phSleep = lifestyleSummary ?? extractHtmlListItem(vikritiBaselineHtml, 'Sleep');

  // Old format: a single personal_history JSON blob, kept as a fallback source
  // for backward compatibility with older compositions.
  const phRaw = parseJsonSection(soapData.personal_history);

  const personalHistoryFields = {
    diet: phDiet ?? getStringField(phRaw?.diet),
    appetite: phAppetite ?? getStringField(phRaw?.appetite),
    sleep: phSleep ?? getStringField(phRaw?.sleep),
    exercise: getStringField(phRaw?.exercise), // no new-schema source; old-format fallback only
    bowel: phBowel ?? getStringField(phRaw?.bowel),
    bladder: getStringField(phRaw?.bladder), // no new-schema source; old-format fallback only
    addiction: getStringField(phRaw?.addiction), // no new-schema source; old-format fallback only
    menstrual: getStringField(phRaw?.menstrual), // no new-schema source; old-format fallback only
  };

  // personalHistory is "present" if at least one field resolved — replaces
  // the old `phRaw ? {...} : null` truthiness check, since there's no longer
  // a single JSON blob to test.
  const personalHistory = Object.values(personalHistoryFields).some((v) => v != null)
    ? personalHistoryFields
    : null;

  const aspRaw = parseJsonSection(soapData.astha_sthaana_pareeksha);
  const asthaSthaanaPareeksha = aspRaw
    ? {
        nadi: getStringField(aspRaw.nadi),
        mootram: getStringField(aspRaw.mootram),
        malam: getStringField(aspRaw.malam),
        jihva: getStringField(aspRaw.jihva),
        sabdam: getStringField(aspRaw.sabdam),
        sparsham: getStringField(aspRaw.sparsham),
        drik: getStringField(aspRaw.drik),
        akrithi: getStringField(aspRaw.akrithi),
      }
    : null;

  const pdRaw = parseJsonSection(soapData.prakruthi_dosha);
  const prakruthiDosha = pdRaw
    ? {
        prakruthi: getStringField(pdRaw.prakruthi),
        dosha: getStringField(pdRaw.dosha),
        aama: getStringField(pdRaw.aama),
      }
    : null;

  const diagRaw = parseJsonSection(soapData.diagnosis);
  const diagnosis = diagRaw
    ? {
        ayurvedic: getStringField(diagRaw.ayurvedic),
        allopathic: getStringField(diagRaw.allopathic),
      }
    : null;

  const treatRaw = parseJsonSection(soapData.treatment);
  const treatment = treatRaw
    ? {
        medicines: getArrayField(treatRaw.medicines),
        treatmentPrinciple: getStringField(treatRaw.treatment_principle),
        treatmentGiven: getStringField(treatRaw.treatment_given),
      }
    : null;

  return {
    chiefComplaint: chiefComplaintText,
    duration,
    modeOfOnset,
    history,
    pastIllness,
    treatmentHistory,
    historyFamilyHistory,
    currentMedication,
    familySocialOccupational,
    objective: parsed.objective as string | null,
    assessment: parsed.assessment as string | null,
    plan: parsed.plan as string | null,
    doshaProfile,
    agni,
    ama,
    nidana,
    dietSummary,
    lifestyleSummary,
    desaInformation,
    registration,
    personalHistory,
    asthaSthaanaPareeksha,
    prakruthiDosha,
    diagnosis,
    treatment,
    commonlyEatenFood: extractCommonlyEatenFood(parsed, dietSummary),
    knownDiseases: extractKnownHistory(parsed),
    familyHistory: extractFamilyHistory(parsed),
  };
}

export function mergeAyurvedaData(
  base: AyurvedaData | null | undefined,
  live: AyurvedaData | null | undefined
): AyurvedaData | null {
  if (!base) return live ?? null;
  if (!live) return base;

  return {
    chiefComplaint: live.chiefComplaint ?? base.chiefComplaint,
    duration: live.duration ?? base.duration,
    modeOfOnset: live.modeOfOnset ?? base.modeOfOnset,
    history: live.history ?? base.history,
    pastIllness: live.pastIllness ?? base.pastIllness,
    treatmentHistory: live.treatmentHistory ?? base.treatmentHistory,
    historyFamilyHistory: live.historyFamilyHistory ?? base.historyFamilyHistory,
    currentMedication: live.currentMedication ?? base.currentMedication,
    familySocialOccupational: live.familySocialOccupational ?? base.familySocialOccupational,
    objective: live.objective ?? base.objective,
    assessment: live.assessment ?? base.assessment,
    plan: live.plan ?? base.plan,
    doshaProfile: live.doshaProfile ?? base.doshaProfile,
    agni: live.agni ?? base.agni,
    ama: live.ama ?? base.ama,
    nidana: live.nidana ?? base.nidana,
    dietSummary: live.dietSummary ?? base.dietSummary,
    lifestyleSummary: live.lifestyleSummary ?? base.lifestyleSummary,
    desaInformation: live.desaInformation ?? base.desaInformation,
    // Base-snapshot fields from the PA Composition. personalHistory is also
    // updated live (personal_history deltas in ConsultationPage.tsx);
    // asthaSthaanaPareeksha has no live source here — the CDSS live stream
    // carries clinical_assessment separately, handled directly in
    // ConsultationPage.tsx.
    registration: live.registration ?? base.registration,
    personalHistory: live.personalHistory ?? base.personalHistory,
    asthaSthaanaPareeksha: live.asthaSthaanaPareeksha ?? base.asthaSthaanaPareeksha,
    prakruthiDosha: live.prakruthiDosha ?? base.prakruthiDosha,
    diagnosis: live.diagnosis ?? base.diagnosis,
    treatment: live.treatment ?? base.treatment,
    commonlyEatenFood:
      live.commonlyEatenFood && live.commonlyEatenFood.length > 0
        ? live.commonlyEatenFood
        : base.commonlyEatenFood,
    knownDiseases:
      live.knownDiseases && live.knownDiseases.length > 0 ? live.knownDiseases : base.knownDiseases,
    familyHistory:
      live.familyHistory && live.familyHistory.length > 0 ? live.familyHistory : base.familyHistory,
  };
}

/**
 * Extract Ayurveda-specific fields from the DiagnosticReport conclusion JSON.
 * Returns null if no Ayurveda data found or if parsing fails.
 */
export function parseAyurvedaFromConclusion(conclusion?: string | null): AyurvedaData | null {
  if (!conclusion) return null;
  try {
    const parsed: unknown = JSON.parse(conclusion);
    if (!isRecord(parsed)) return null;

    // Check for Ayurveda-specific fields in the response
    const hasAyurvedaFields =
      parsed.ayurveda_context || parsed.desa_information || parsed.personal_history;
    if (!hasAyurvedaFields) return null;

    // Extract from ayurveda_context
    const ac = isRecord(parsed.ayurveda_context) ? parsed.ayurveda_context : null;
    let doshaProfile: AyurvedaData['doshaProfile'] = null;
    let agni: string | null = null;
    let ama: string | null = null;
    let nidana: string | null = null;
    let dietSummary: string | null = null;
    let lifestyleSummary: string | null = null;

    if (ac) {
      doshaProfile = {
        prakriti: getStringField(ac.prakriti),
        vikriti: getStringField(ac.vikriti),
        manasikGuna: getStringField(ac.manasik_guna ?? ac.manasikGuna),
      };
      agni = getStringField(ac.agni_status ?? ac.agni);
      ama = getStringField(ac.ama_grade ?? ac.ama);
      nidana = getStringField(ac.nidana);
      dietSummary = getStringField(ac.diet_summary ?? ac.dietSummary);
      lifestyleSummary = getStringField(ac.lifestyle_summary ?? ac.lifestyleSummary);
    }

    // Extract desa_information
    const di = isRecord(parsed.desa_information) ? parsed.desa_information : null;
    let desaInformation: AyurvedaData['desaInformation'] = null;
    if (di) {
      const regionalDietRaw = isRecord(di.regional_diet) ? di.regional_diet : null;
      desaInformation = {
        state: getStringField(di.state),
        city: getStringField(di.city),
        season: getStringField(di.season),
        desaType: getStringField(di.desa_type ?? di.desaType),
        temperature: typeof di.temperature === 'number' ? di.temperature : null,
        humidity: typeof di.humidity === 'number' ? di.humidity : null,
        stapleDiet: getStringField(di.staple_diet ?? di.stapleDiet),
        regionalDiet: regionalDietRaw
          ? {
              grains: getStringField(regionalDietRaw.grains),
              pulses: getStringField(regionalDietRaw.pulses),
              fruits: getStringField(regionalDietRaw.fruits),
              veg: getStringField(regionalDietRaw.veg),
              dairy: getStringField(regionalDietRaw.dairy),
              spiceLevel:
                typeof regionalDietRaw.spice_level === 'number'
                  ? regionalDietRaw.spice_level
                  : null,
            }
          : null,
      };
    }

    // Extract personal_history — same structured shape the kiosk agent writes,
    // just sourced from a DiagnosticReport conclusion instead of a Composition
    // section. Previously missing entirely from this parser, so `addiction`
    // (and the rest of the Personal History card) never populated for any
    // CDSS-only consultation, even after the session ended and saved.
    const ph = isRecord(parsed.personal_history) ? parsed.personal_history : null;
    let personalHistory: AyurvedaData['personalHistory'] = null;
    if (ph) {
      personalHistory = {
        diet: getStringField(ph.diet),
        appetite: getStringField(ph.appetite),
        sleep: getStringField(ph.sleep),
        exercise: getStringField(ph.exercise),
        bowel: getStringField(ph.bowel),
        bladder: getStringField(ph.bladder),
        addiction: getStringField(ph.addiction),
        menstrual: getStringField(ph.menstrual),
      };
    }

    const subjective = getStringField(parsed.subjective);
    const objective = getStringField(parsed.objective);
    const assessment = getStringField(parsed.assessment);
    const plan = getStringField(parsed.plan);

    const explicitCommonlyEatenFood = getArrayField(
      parsed.commonly_eaten_food ?? parsed.commonlyEatenFood ?? ac?.commonly_eaten_food
    );
    const explicitKnownDiseases = getArrayField(
      parsed.known_diseases ?? parsed.knownDiseases ?? ac?.known_diseases
    );
    const explicitFamilyHistory = getArrayField(
      parsed.family_history ?? parsed.familyHistory ?? ac?.family_history
    );

    logger.log('[Ayurveda] Parsed data from DiagnosticReport:', {
      doshaProfile,
      agni,
      ama,
      nidana,
      dietSummary,
      lifestyleSummary,
      desaInformation,
      personalHistory,
      subjective,
      assessment,
      plan,
    });

    return {
      chiefComplaint: subjective,
      objective,
      assessment,
      plan,
      doshaProfile,
      agni,
      ama,
      nidana,
      dietSummary,
      lifestyleSummary,
      desaInformation,
      personalHistory,
      commonlyEatenFood:
        explicitCommonlyEatenFood.length > 0
          ? explicitCommonlyEatenFood
          : extractCommonlyEatenFood(parsed, dietSummary),
      knownDiseases:
        explicitKnownDiseases.length > 0 ? explicitKnownDiseases : extractKnownHistory(parsed),
      familyHistory:
        explicitFamilyHistory.length > 0 ? explicitFamilyHistory : extractFamilyHistory(parsed),
    };
  } catch (e) {
    logger.error('[Ayurveda] Failed to parse conclusion:', e);
    return null;
  }
}
